export type ApiConnectionPhase = 'connecting' | 'waking' | 'ready' | 'unavailable';

export interface WakeRetryOptions {
  signal?: AbortSignal;
  wakeAfterMs?: number;
  maxWaitMs?: number;
  retryDelayMs?: number;
  requestTimeoutMs?: number;
  retryStatusCodes?: readonly number[];
  onPhaseChange?: (phase: ApiConnectionPhase) => void;
}

const defaultRetryStatusCodes = [408, 425, 429, 500, 502, 503, 504] as const;

function wait(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.resolve();

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);

    const handleAbort = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    };

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  const abortFromCaller = () => controller.abort();
  const headers = new Headers(init.headers);

  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  signal?.addEventListener('abort', abortFromCaller, { once: true });

  try {
    return await fetch(input, {
      ...init,
      cache: init.cache ?? 'no-store',
      headers,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export async function fetchWithWakeRetry(
  input: string,
  init: RequestInit = {},
  options: WakeRetryOptions = {},
): Promise<Response | null> {
  const wakeAfterMs = options.wakeAfterMs ?? 7_000;
  const maxWaitMs = options.maxWaitMs ?? 75_000;
  const retryDelayMs = options.retryDelayMs ?? 4_000;
  const requestTimeoutMs = options.requestTimeoutMs ?? 12_000;
  const retryStatusCodes = new Set(options.retryStatusCodes ?? defaultRetryStatusCodes);
  const startedAt = Date.now();
  let phase: ApiConnectionPhase = 'connecting';

  const emitPhase = (nextPhase: ApiConnectionPhase) => {
    if (phase === nextPhase) return;
    phase = nextPhase;
    options.onPhaseChange?.(nextPhase);
  };

  options.onPhaseChange?.(phase);

  const wakeTimer = window.setTimeout(() => {
    if (!options.signal?.aborted) emitPhase('waking');
  }, wakeAfterMs);

  try {
    while (!options.signal?.aborted && Date.now() - startedAt < maxWaitMs) {
      const remainingMs = maxWaitMs - (Date.now() - startedAt);
      const timeoutMs = Math.max(1, Math.min(requestTimeoutMs, remainingMs));

      try {
        const response = await fetchWithTimeout(input, init, timeoutMs, options.signal);

        if (response.ok || !retryStatusCodes.has(response.status)) {
          emitPhase('ready');
          return response;
        }
      } catch {
        if (options.signal?.aborted) return null;
      }

      if (Date.now() - startedAt >= wakeAfterMs) emitPhase('waking');

      const remainingAfterAttempt = maxWaitMs - (Date.now() - startedAt);
      if (remainingAfterAttempt <= 0) break;
      await wait(Math.min(retryDelayMs, remainingAfterAttempt), options.signal);
    }

    if (!options.signal?.aborted) emitPhase('unavailable');
    return null;
  } finally {
    window.clearTimeout(wakeTimer);
  }
}

export async function fetchJsonWithWakeRetry<T>(
  input: string,
  options: WakeRetryOptions = {},
): Promise<T | null> {
  const response = await fetchWithWakeRetry(input, {}, options);
  if (!response?.ok) return null;

  try {
    return await response.json() as T;
  } catch {
    return null;
  }
}
